import { isPlatformBrowser } from '@angular/common';
import type {
  AfterViewInit,
  ElementRef,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  PLATFORM_ID,
  ViewChildren,
  inject,
} from '@angular/core';
import type { FormArray } from '@angular/forms';
import { FormControl, NonNullableFormBuilder } from '@angular/forms';
import { shakeAnimation } from '@freelancer/animations';
import { TimeUtils } from '@freelancer/time-utils';
import { pattern } from '@freelancer/ui/validators';
import { trackByIndex } from '../helpers';
import { OtpInputSize, OtpInputSpacing } from './otp-input.types';

@Component({
  standalone: true,
  selector: 'fl-otp-input',
  template: `
    <div
      class="OtpInput"
      [class.OtpInput--disabled]="isDisabled"
      [@shakeAnimation]="{
        value: shakeState,
        params: {
          duration: 200,
          translateX: '3px',
        },
      }"
      (@shakeAnimation.done)="onShakeEnd()"
    >
      @for (
        control of codeInputFormArray.controls;
        track trackByIndex(i);
        let i = $index
      ) {
        <input
          #otpInputElement
          type="number"
          autocomplete="one-time-code"
          class="OtpInputItem"
          [class.OtpInputItem--filled]="control.value !== ''"
          [class.OtpInputItem--focused]="focusedIndex === i"
          [class.OtpInputItem--error]="hasError"
          [attr.id]="'otpInputElement-' + i.toString()"
          [attr.aria-label]="'Digit ' + (i + 1) + ' of ' + codeLength"
          [attr.maxlength]="1"
          [attr.disabled]="isDisabled ? true : null"
          [value]="control.value"
          (paste)="handlePasteEvent($event)"
          (keydown)="handleKeyboardInputEvent($event, i)"
          (input)="handleInputEvent($event, i)"
          (focus)="handleFocus(i)"
          (blur)="handleBlur()"
        />
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './otp-input.component.scss',
  animations: [shakeAnimation],
})
export class OtpInputComponent implements AfterViewInit, OnChanges, OnInit {
  private cd = inject(ChangeDetectorRef);
  private fb = inject(NonNullableFormBuilder);
  private platformId = inject(PLATFORM_ID);
  private timeUtils = inject(TimeUtils);

  trackByIndex = trackByIndex;
  focusedIndex: number | undefined = undefined;
  shakeState: 'active' | 'void' = 'void';

  /** Code length is the number of digits in the code. */
  @Input() codeLength = 6;
  @Input() isDisabled = false;
  @Input() hasError = false;
  @Input() disableAutoFocus = false;
  /** Determines the size of the input. */
  @Input() @HostBinding('attr.data-size') size: OtpInputSize = OtpInputSize.MID;
  /** Determines the spacing between the input digits. */
  @Input() @HostBinding('attr.data-spacing') spacing: OtpInputSpacing =
    OtpInputSpacing.AUTO;

  /** Emits when the code is complete. */
  @Output() codeComplete = new EventEmitter<string>();
  /** Emits when the error state is cleared. */
  @Output() errorCleared = new EventEmitter<void>();

  @ViewChildren('otpInputElement') otpInputElements: QueryList<
    ElementRef<HTMLInputElement>
  >;

  codeInputFormArray: FormArray<FormControl<string>>;

  ngOnInit(): void {
    this.codeInputFormArray = this.fb.array(
      Array.from(
        { length: this.codeLength },
        () =>
          new FormControl<string>('', {
            nonNullable: true,
            validators: pattern(/^[0-9]$/, ''),
          }),
      ),
    );
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId) && !this.disableAutoFocus) {
      this.focusToIndex(0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const errorChange = changes.hasError;
    if (errorChange?.currentValue && !errorChange.previousValue) {
      this.triggerError();
    }
  }

  private triggerError(): void {
    this.shakeState = 'active';
    this.codeInputFormArray.controls.forEach(control => {
      control.setValue('');
    });
    if (isPlatformBrowser(this.platformId)) {
      this.focusToIndex(0);
    }
    this.cd.markForCheck();
  }

  onShakeEnd(): void {
    this.shakeState = 'void';
    this.cd.markForCheck();
  }

  private clearErrorState(): void {
    this.shakeState = 'void';
    this.errorCleared.emit();
  }

  handlePasteEvent(event: ClipboardEvent): void {
    event.preventDefault();
    this.clearErrorState();

    const textData = event.clipboardData?.getData('text')?.trim();
    if (!textData) {
      return;
    }

    const digits = textData.replace(/\D/g, '');
    if (digits.length === 0) {
      return;
    }

    const charsToFill = digits.slice(0, this.codeLength).split('');
    charsToFill.forEach((char, i) => {
      this.codeInputFormArray.at(i).setValue(char);
    });

    const nextIndex = Math.min(charsToFill.length, this.codeLength - 1);
    this.focusToIndex(nextIndex);

    this.emitCodeIfComplete();
    this.cd.markForCheck();
  }

  handleDigitInput(digit: string, index: number): void {
    this.clearErrorState();
    this.codeInputFormArray.at(index).setValue(digit);

    if (index < this.codeLength - 1) {
      this.focusToIndex(index + 1);
    }

    this.emitCodeIfComplete();
  }

  handleKeyboardInputEvent(event: KeyboardEvent, index: number): void {
    const { key, ctrlKey, metaKey } = event;

    if ((ctrlKey || metaKey) && key.toLowerCase() === 'v') {
      return;
    }

    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      this.handleDigitInput(key, index);
      return;
    }

    if (key === 'Backspace' || key === 'Delete') {
      event.preventDefault();
      this.clearErrorState();
      const currentValue = this.codeInputFormArray.at(index).value;

      if (currentValue === '' && index > 0) {
        this.codeInputFormArray.at(index - 1).setValue('');
        this.focusToIndex(index - 1);
      } else {
        this.codeInputFormArray.at(index).setValue('');
      }
      this.cd.markForCheck();
      return;
    }

    if (key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusToIndex(index - 1);
      return;
    }

    if (key === 'ArrowRight' && index < this.codeLength - 1) {
      event.preventDefault();
      this.focusToIndex(index + 1);
      return;
    }

    if (key === 'Tab') {
      return;
    }

    event.preventDefault();
  }

  handleInputEvent(event: Event, index: number): void {
    const { value } = event.target as HTMLInputElement;

    if (value.length > 1) {
      this.clearErrorState();
      const digits = value.replace(/\D/g, '');
      if (digits.length > 0) {
        digits.split('').forEach((char, i) => {
          const targetIndex = index + i;
          if (targetIndex < this.codeLength) {
            this.codeInputFormArray.at(targetIndex).setValue(char);
          }
        });
        const nextIndex = Math.min(index + digits.length, this.codeLength - 1);
        this.focusToIndex(nextIndex);
        this.emitCodeIfComplete();
      }
      this.cd.markForCheck();
      return;
    }

    if (value.length === 1 && /^[0-9]$/.test(value)) {
      this.clearErrorState();
      this.codeInputFormArray.at(index).setValue(value);
      if (index < this.codeLength - 1) {
        this.focusToIndex(index + 1);
      }
      this.emitCodeIfComplete();
    } else if (value === '') {
      this.codeInputFormArray.at(index).setValue('');
    }

    this.cd.markForCheck();
  }

  handleFocus(index: number): void {
    this.focusedIndex = index;
    if (isPlatformBrowser(this.platformId)) {
      const inputEl = this.otpInputElements?.toArray()[index]?.nativeElement;
      if (inputEl) {
        inputEl.select();
      }
    }
  }

  handleBlur(): void {
    this.focusedIndex = undefined;
  }

  focusToIndex(index: number): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.timeUtils.setTimeout(() => {
      const inputEl = this.otpInputElements?.toArray()[index]?.nativeElement;
      if (inputEl && !this.isDisabled) {
        inputEl.focus();
        inputEl.select();
      }
    });
  }

  getCurrentCode(): string | undefined {
    if (!this.codeInputFormArray) {
      return undefined;
    }

    const values = this.codeInputFormArray.controls.map(c => c.value);
    const allFilled = values.every(v => v !== '');
    const allValid = this.codeInputFormArray.valid;

    return allFilled && allValid ? values.join('') : undefined;
  }

  /** Resets all inputs and focuses the first one */
  reset(): void {
    this.shakeState = 'void';
    this.codeInputFormArray.controls.forEach(control => {
      control.setValue('');
      control.markAsUntouched();
      control.markAsPristine();
    });
    this.focusToIndex(0);
    this.cd.markForCheck();
  }

  private emitCodeIfComplete(): void {
    const code = this.getCurrentCode();
    if (code !== undefined) {
      this.codeComplete.emit(code);
    }
  }
}
